import { useState, useCallback, useRef, useEffect } from 'react';

interface LoadingState {
  [key: string]: boolean;
}

interface UseLoadingStateReturn {
  loading: LoadingState;
  isLoading: (key?: string) => boolean;
  isAnyLoading: () => boolean;
  setLoading: (key: string, loading: boolean) => void;
  startLoading: (key: string) => void;
  stopLoading: (key: string) => void;
  withLoading: <T>(
    key: string,
    operation: () => Promise<T>
  ) => Promise<T>;
  clearAll: () => void;
}

export function useLoadingState(initialState: LoadingState = {}): UseLoadingStateReturn {
  const [loading, setLoadingState] = useState<LoadingState>(initialState);
  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      timeoutsRef.current.clear();
    };
  }, []);

  const isLoading = useCallback((key?: string) => {
    if (!key) return Object.values(loading).some(Boolean);
    return loading[key] || false;
  }, [loading]);

  const isAnyLoading = useCallback(() => {
    return Object.values(loading).some(Boolean);
  }, [loading]);

  const setLoading = useCallback((key: string, isLoading: boolean) => {
    // Clear any existing timeout for this key
    const existingTimeout = timeoutsRef.current.get(key);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      timeoutsRef.current.delete(key);
    }

    setLoadingState(prev => ({
      ...prev,
      [key]: isLoading,
    }));

    // Set a safety timeout to prevent stuck loading states
    if (isLoading) {
      const timeout = setTimeout(() => {
        console.warn(`Loading state for "${key}" has been active for over 30 seconds. Auto-clearing.`);
        setLoadingState(prev => ({
          ...prev,
          [key]: false,
        }));
        timeoutsRef.current.delete(key);
      }, 30000); // 30 seconds

      timeoutsRef.current.set(key, timeout);
    }
  }, []);

  const startLoading = useCallback((key: string) => {
    setLoading(key, true);
  }, [setLoading]);

  const stopLoading = useCallback((key: string) => {
    setLoading(key, false);
  }, [setLoading]);

  const withLoading = useCallback(async <T,>(
    key: string,
    operation: () => Promise<T>
  ): Promise<T> => {
    try {
      startLoading(key);
      const result = await operation();
      return result;
    } finally {
      stopLoading(key);
    }
  }, [startLoading, stopLoading]);

  const clearAll = useCallback(() => {
    // Clear all timeouts
    timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    timeoutsRef.current.clear();
    
    // Clear all loading states
    setLoadingState({});
  }, []);

  return {
    loading,
    isLoading,
    isAnyLoading,
    setLoading,
    startLoading,
    stopLoading,
    withLoading,
    clearAll,
  };
}

// Global loading state context
import { createContext, useContext, type ReactNode } from 'react';

interface GlobalLoadingContextType extends UseLoadingStateReturn {
  // Additional global loading methods can be added here
}

const GlobalLoadingContext = createContext<GlobalLoadingContextType | undefined>(undefined);

interface GlobalLoadingProviderProps {
  children: ReactNode;
}

export function GlobalLoadingProvider({ children }: GlobalLoadingProviderProps) {
  const loadingState = useLoadingState();

  return (
    <GlobalLoadingContext.Provider value={loadingState}>
      {children}
    </GlobalLoadingContext.Provider>
  );
}

export function useGlobalLoading(): GlobalLoadingContextType {
  const context = useContext(GlobalLoadingContext);
  if (!context) {
    throw new Error('useGlobalLoading must be used within a GlobalLoadingProvider');
  }
  return context;
}

// Hook for component-specific loading states with automatic cleanup
export function useComponentLoading(componentName: string) {
  const globalLoading = useGlobalLoading();
  
  const setLoading = useCallback((operation: string, loading: boolean) => {
    const key = `${componentName}.${operation}`;
    globalLoading.setLoading(key, loading);
  }, [componentName, globalLoading]);

  const isLoading = useCallback((operation?: string) => {
    if (!operation) {
      // Check if any operation for this component is loading
      return Object.keys(globalLoading.loading).some(key => 
        key.startsWith(`${componentName}.`) && globalLoading.loading[key]
      );
    }
    const key = `${componentName}.${operation}`;
    return globalLoading.isLoading(key);
  }, [componentName, globalLoading]);

  const withLoading = useCallback(async <T,>(
    operation: string,
    task: () => Promise<T>
  ): Promise<T> => {
    const key = `${componentName}.${operation}`;
    return globalLoading.withLoading(key, task);
  }, [componentName, globalLoading]);

  // Cleanup component loading states on unmount
  useEffect(() => {
    return () => {
      Object.keys(globalLoading.loading).forEach(key => {
        if (key.startsWith(`${componentName}.`)) {
          globalLoading.setLoading(key, false);
        }
      });
    };
  }, [componentName, globalLoading]);

  return {
    setLoading,
    isLoading,
    withLoading,
    startLoading: (operation: string) => setLoading(operation, true),
    stopLoading: (operation: string) => setLoading(operation, false),
  };
}